﻿import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { DateTimePicker } from "@/components/datetime-picker";
import { ImageUploader } from "@/components/image-uploader";
import { LocationPicker } from "@/components/location-picker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n";

/**
 * Create/edit event form. Backend event slug'ı Title'dan üretir; create'te kullanıcı override edebilir.
 * Tarihler datetime-local formatında alınır, submit'te ISO string olarak gönderilir.
 * TicketType ekleme/silme ayrı komponentte — bu form sadece core event meta'sını yönetir.
 *
 * Cover image ele alınışı:
 *  - Edit modda eventId mevcut → ImageUploader immediate, dönen server URL form'a yazılır.
 *  - Create modda eventId yok → ImageUploader deferred, file local state'te tutulur. Submit
 *    sonrası NewEventPage create+upload+update sırasıyla orchestrate eder.
 */
const baseSchema = {
  title: z.string().min(2, "validation.titleMin").max(200),
  description: z.string().max(10000).optional().or(z.literal("")),
  venueName: z.string().max(200).optional().or(z.literal("")),
  venueAddress: z.string().max(500).optional().or(z.literal("")),
  venueCity: z.string().max(100).optional().or(z.literal("")),
  // Harita picker set eder. Manuel girilemez (LocationPicker readonly gösterir).
  latitude: z
    .number()
    .min(-90)
    .max(90)
    .nullable()
    .optional(),
  longitude: z
    .number()
    .min(-180)
    .max(180)
    .nullable()
    .optional(),
  startsAt: z.string().min(1, "validation.startsRequired"),
  endsAt: z.string().min(1, "validation.endsRequired"),
  defaultTaxRate: z.coerce
    .number()
    .min(0, "validation.taxMin")
    .max(100, "validation.taxMax"),
};

// datetime-local string'i lexicographic karşılaştırılabilir (ISO benzeri "YYYY-MM-DDTHH:mm").
// Boş alanları geçiyoruz; required kontrolü min(1) ile zaten yapılıyor → burada sadece
// ikisi de doluysa sıralamayı zorluyoruz.
const endsAfterStarts = (data: { startsAt?: string; endsAt?: string }) =>
  !data.startsAt || !data.endsAt || data.endsAt > data.startsAt;

const createSchema = z
  .object({
    ...baseSchema,
    slug: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "validation.slugPattern")
      .min(3)
      .max(80)
      .optional()
      .or(z.literal("")),
  })
  .refine(endsAfterStarts, {
    message: "validation.endsAfterStarts",
    path: ["endsAt"],
  });

const editSchema = z
  .object({
    ...baseSchema,
    coverImageUrl: z.string().url("validation.urlInvalid").optional().or(z.literal("")),
  })
  .refine(endsAfterStarts, {
    message: "validation.endsAfterStarts",
    path: ["endsAt"],
  });

export type CreateEventFormValues = z.infer<typeof createSchema>;
export type EditEventFormValues = z.infer<typeof editSchema>;
type CreateEventFormInput = z.input<typeof createSchema>;
type EditEventFormInput = z.input<typeof editSchema>;

type EventFormProps =
  | {
      mode: "create";
      defaultValues?: Partial<CreateEventFormValues>;
      /**
       * Submit handler — form değerleri ve (varsa) seçilen kapak dosyası birlikte gelir.
       * NewEventPage önce event create eder, eventId aldıktan sonra dosyayı upload eder
       * ve son olarak coverImageUrl'i PATCH ile yazar.
       */
      onSubmit: (values: CreateEventFormValues, coverFile: File | null) => Promise<void> | void;
      isSubmitting?: boolean;
      submitLabel?: string;
    }
  | {
      mode: "edit";
      /** Edit modda zorunlu — ImageUploader bu id ile S3 key'i türetir ve ownership check yapar. */
      eventId: string;
      defaultValues?: Partial<EditEventFormValues>;
      onSubmit: (values: EditEventFormValues) => Promise<void> | void;
      isSubmitting?: boolean;
      submitLabel?: string;
    };

export function EventForm(props: EventFormProps) {
  if (props.mode === "create") {
    return <CreateFormImpl {...props} />;
  }
  return <EditFormImpl {...props} />;
}

type CreateFormProps = Extract<EventFormProps, { mode: "create" }>;

function CreateFormImpl({ defaultValues, onSubmit, isSubmitting, submitLabel }: CreateFormProps) {
  const t = useT("eventForm");
  const form = useForm<CreateEventFormInput, unknown, CreateEventFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      title: "",
      description: "",
      slug: "",
      venueName: "",
      venueAddress: "",
      venueCity: "",
      latitude: null,
      longitude: null,
      startsAt: "",
      endsAt: "",
      defaultTaxRate: 20,
      ...defaultValues,
    },
  });

  // Create akışında eventId henüz olmadığı için kapak dosyası form-dışı state'te tutulur;
  // submit anında parent (NewEventPage) önce event create eder, sonra dosyayı upload eder.
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((values) => onSubmit(values, coverFile))}
        className="space-y-6"
      >
        <CoreFields
          form={form}
          imageMode={{
            kind: "create",
            previewUrl: coverPreviewUrl,
            onPreviewChange: setCoverPreviewUrl,
            onFileChange: setCoverFile,
          }}
        />

        <FormField
          control={form.control}
          name="slug"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("slug")}</FormLabel>
              <FormControl>
                <Input placeholder={t("slugPlaceholder")} dir="ltr" {...field} />
              </FormControl>
              <FormDescription>
                {t("slugPreview")} <span className="font-mono" dir="ltr">/events/{field.value || t("slugAuto")}</span>
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <VenueFields form={form} />
        <DateFields form={form} />

        <SubmitRow isSubmitting={isSubmitting} label={submitLabel ?? t("createSubmit")} />
      </form>
    </Form>
  );
}

type EditFormProps = Extract<EventFormProps, { mode: "edit" }>;

function EditFormImpl({ eventId, defaultValues, onSubmit, isSubmitting, submitLabel }: EditFormProps) {
  const t = useT("eventForm");
  const form = useForm<EditEventFormInput, unknown, EditEventFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      title: "",
      description: "",
      coverImageUrl: "",
      venueName: "",
      venueAddress: "",
      venueCity: "",
      latitude: null,
      longitude: null,
      startsAt: "",
      endsAt: "",
      defaultTaxRate: 20,
      ...defaultValues,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <CoreFields form={form} imageMode={{ kind: "edit", eventId }} />
        <VenueFields form={form} />
        <DateFields form={form} />

        <SubmitRow isSubmitting={isSubmitting} label={submitLabel ?? t("editSubmit")} />
      </form>
    </Form>
  );
}

// ---- Alt bölümler ----

type AnyForm =
  | UseFormReturn<CreateEventFormInput, unknown, CreateEventFormValues>
  | UseFormReturn<EditEventFormInput, unknown, EditEventFormValues>;

/**
 * İki form varyantında da var olan ortak alanlar. RHF'in Control<T>/watch()
 * imzaları T üzerinde invariant olduğu için union form (AnyForm) doğrudan
 * `form.control` ile FormField'a verilemiyor — alt komponentler bu ortak
 * tipe cast eder. slug (Create) ve coverImageUrl (Edit) bu alanların
 * dışında, kendi parent komponentlerinde / EditCoverField içinde kalır.
 */
type CommonEventFormInput = {
  title: string;
  description?: string;
  venueName?: string;
  venueAddress?: string;
  venueCity?: string;
  latitude?: number | null;
  longitude?: number | null;
  startsAt: string;
  endsAt: string;
  defaultTaxRate: unknown;
};
type CommonForm = UseFormReturn<CommonEventFormInput>;

type ImageMode =
  | { kind: "edit"; eventId: string }
  | {
      kind: "create";
      previewUrl: string | null;
      onPreviewChange: (url: string | null) => void;
      onFileChange: (file: File | null) => void;
    };

function CoreFields({ form, imageMode }: { form: AnyForm; imageMode: ImageMode }) {
  const t = useT("eventForm");
  const f = form as unknown as CommonForm;
  return (
    <div className="space-y-5">
      <FormField
        control={f.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("title")}</FormLabel>
            <FormControl>
              <Input placeholder={t("titlePlaceholder")} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={f.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("description")}</FormLabel>
            <FormControl>
              <Textarea
                rows={5}
                placeholder={t("descriptionPlaceholder")}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormItem>
        <FormLabel>{t("cover")}</FormLabel>
        <FormControl>
          {imageMode.kind === "edit" ? (
            <EditCoverField form={form} eventId={imageMode.eventId} />
          ) : (
            <ImageUploader
              deferred
              aspect="cover"
              value={imageMode.previewUrl}
              onChange={imageMode.onPreviewChange}
              onFileSelect={imageMode.onFileChange}
            />
          )}
        </FormControl>
        <FormDescription>{t("coverHint")}</FormDescription>
      </FormItem>
    </div>
  );
}

function EditCoverField({ form, eventId }: { form: AnyForm; eventId: string }) {
  // Edit modunda eventId mevcut → ImageUploader doğrudan upload eder, dönen URL'yi
  // form'un coverImageUrl alanına yazar. RHF Controller pattern'i yerine watch+setValue
  // ile bağlamak union form tipinde generic narrowing problemini önlüyor.
  const editForm = form as unknown as UseFormReturn<
    EditEventFormInput,
    unknown,
    EditEventFormValues
  >;
  const value = editForm.watch("coverImageUrl") ?? "";
  return (
    <ImageUploader
      kind="EventCover"
      eventId={eventId}
      aspect="cover"
      value={typeof value === "string" ? value : ""}
      onChange={(url) =>
        editForm.setValue("coverImageUrl", url ?? "", { shouldDirty: true })
      }
    />
  );
}

function VenueFields({ form }: { form: AnyForm }) {
  const t = useT("eventForm");
  // `AnyForm` iki form varyantının union'u; TS union narrowing setValue/watch
  // imzalarını intersect edemediği için ortak alanları içeren CommonForm'a
  // cast ediyoruz. Her iki varyant da aynı venue alanlarını içeriyor → güvenli.
  const f = form as unknown as CommonForm;
  const latitude = f.watch("latitude") ?? null;
  const longitude = f.watch("longitude") ?? null;

  // LocationPicker seçim yapınca: lat/lon + adres + şehir otomatik güncellenir.
  // Eski Biletzero projesindeki davranışla hizalı — haritadan seçim tüm alanları
  // override eder. Kullanıcı manuel yazmak isterse haritada başka nokta seçmez.
  const handleLocationChange = (
    coords: {
      latitude: number;
      longitude: number;
      address?: string | null;
      city?: string | null;
    } | null,
  ) => {
    if (coords) {
      f.setValue("latitude", coords.latitude, { shouldDirty: true });
      f.setValue("longitude", coords.longitude, { shouldDirty: true });
      if (coords.address) {
        f.setValue("venueAddress", coords.address, { shouldDirty: true });
      }
      // Şehir sadece boşsa doldur — kullanıcı özellikle "Kadıköy" yazdıysa
      // Nominatim'in "İstanbul" dönüşü bunu ezmesin.
      const currentCity = f.getValues("venueCity") ?? "";
      if (!currentCity && coords.city) {
        f.setValue("venueCity", coords.city, { shouldDirty: true });
      }
    } else {
      f.setValue("latitude", null, { shouldDirty: true });
      f.setValue("longitude", null, { shouldDirty: true });
    }
  };

  return (
    <div className="space-y-5 rounded-lg border p-4">
      <h3 className="text-sm font-semibold">{t("venueHeading")}</h3>
      <p className="text-xs text-muted-foreground">{t("venueHint")}</p>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          control={f.control}
          name="venueName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("venueName")}</FormLabel>
              <FormControl>
                <Input placeholder={t("venueNamePlaceholder")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={f.control}
          name="venueCity"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("city")}</FormLabel>
              <FormControl>
                <Input placeholder={t("cityPlaceholder")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={f.control}
        name="venueAddress"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("address")}</FormLabel>
            <FormControl>
              <Input placeholder={t("addressPlaceholder")} {...field} />
            </FormControl>
            <FormDescription>{t("addressHint")}</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormItem>
        <FormLabel>{t("location")}</FormLabel>
        <LocationPicker
          latitude={typeof latitude === "number" ? latitude : null}
          longitude={typeof longitude === "number" ? longitude : null}
          onChange={handleLocationChange}
        />
      </FormItem>
    </div>
  );
}

function DateFields({ form }: { form: AnyForm }) {
  const t = useT("eventForm");
  // Bitiş başlangıca bağlı: başlangıç seçilmeden bitiş aktif olmaz; seçildiğinde
  // bitişin minimumu başlangıç olur (öncesi tarayıcı picker'ında ve native
  // validation'da reddedilir). Cross-field zod refine, manuel girişte de fallback.
  const f = form as unknown as CommonForm;
  const startsAt = f.watch("startsAt");
  const startsAtStr = typeof startsAt === "string" ? startsAt : "";

  return (
    <div className="space-y-5 rounded-lg border p-4">
      <h3 className="text-sm font-semibold">{t("datesHeading")}</h3>
      <p className="text-xs text-muted-foreground">{t("datesHint")}</p>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          control={f.control}
          name="startsAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("startsAt")}</FormLabel>
              <FormControl>
                <DateTimePicker
                  value={typeof field.value === "string" ? field.value : ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={f.control}
          name="endsAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("endsAt")}</FormLabel>
              <FormControl>
                <DateTimePicker
                  value={typeof field.value === "string" ? field.value : ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  disabled={!startsAtStr}
                  min={startsAtStr || undefined}
                  placeholder={startsAtStr ? undefined : t("pickStartFirst")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={f.control}
        name="defaultTaxRate"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("tax")}</FormLabel>
            <FormControl>
              <Input
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step="0.5"
                {...field}
                value={
                  typeof field.value === "number" || typeof field.value === "string"
                    ? field.value
                    : 20
                }
              />
            </FormControl>
            <FormDescription>{t("taxHint")}</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function SubmitRow({ isSubmitting, label }: { isSubmitting?: boolean; label: string }) {
  const tCommon = useT("common");
  return (
    <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
      {isSubmitting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
      {isSubmitting ? tCommon("saving") : label}
    </Button>
  );
}
